import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FlowCanvas } from '../../components/Canvas/FlowCanvas';
import { canvasService } from '../../services/canvasService';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { UserMenu } from '../../components/Layout/UserMenu';
import { Logo } from '../../components/ui/Logo';
import { useAuthStore } from '../../stores/authStore';
import { useOrgStore } from '../../stores/orgStore';
import { Copy, Lock, Eye } from 'lucide-react';

type ViewState = 'loading' | 'password' | 'viewing' | 'not_found' | 'error';

export function SharedCanvasPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const orgId = useOrgStore((s) => s.activeOrgId);

  const [viewState, setViewState] = useState<ViewState>('loading');
  const [canvasName, setCanvasName] = useState('');
  const [canvasData, setCanvasData] = useState<any>(null);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [duplicateSuccess, setDuplicateSuccess] = useState(false);

  // Load shared canvas metadata
  useEffect(() => {
    if (!token) return;
    setViewState('loading');

    canvasService.getSharedMeta(token)
      .then(({ data }: any) => {
        setCanvasName(data.name);
        if (data.requiresPassword) {
          setViewState('password');
        } else {
          // Public — load full data immediately
          return canvasService.accessShared(token).then(({ data: fullData }: any) => {
            setCanvasData(fullData);
            setViewState('viewing');
          });
        }
      })
      .catch((err: any) => {
        if (err.message?.includes('not found')) {
          setViewState('not_found');
        } else {
          setViewState('error');
        }
      });
  }, [token]);

  const handlePasswordSubmit = async () => {
    if (!token || !password.trim()) return;
    setIsVerifying(true);
    setPasswordError('');
    try {
      const { data } = await canvasService.accessShared(token, password.trim());
      setCanvasData(data);
      setViewState('viewing');
    } catch (err: any) {
      setPasswordError(err.message === 'Incorrect password' ? 'Wrong password. Try again.' : 'Failed to access canvas.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleDuplicate = async () => {
    if (!token || !orgId) return;
    setIsDuplicating(true);
    try {
      const { data } = await canvasService.duplicateShared(token, password || undefined);
      setDuplicateSuccess(true);
      // Navigate to the duplicated canvas after a short delay
      setTimeout(() => navigate(`/canvas/${data.canvas.id}`), 1200);
    } catch (err: any) {
      console.error('Failed to duplicate:', err);
    } finally {
      setIsDuplicating(false);
    }
  };

  // Password prompt screen
  if (viewState === 'password') {
    return (
      <div className="h-screen flex flex-col">
        <SharedHeader canvasName={canvasName} />
        <div className="flex-1 flex items-center justify-center bg-gray-50">
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 max-w-sm w-full mx-4">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 mx-auto mb-4">
              <Lock className="w-6 h-6 text-amber-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-800 text-center mb-1">{canvasName}</h2>
            <p className="text-sm text-gray-500 text-center mb-6">This canvas is password protected.</p>
            <form onSubmit={(e) => { e.preventDefault(); handlePasswordSubmit(); }} className="space-y-3">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password..."
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                autoFocus
              />
              {passwordError && <p className="text-xs text-red-500">{passwordError}</p>}
              <Button variant="primary" size="md" className="w-full" isLoading={isVerifying} type="submit">
                View Canvas
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Not found / error
  if (viewState === 'not_found' || viewState === 'error') {
    return (
      <div className="h-screen flex flex-col">
        <SharedHeader />
        <div className="flex-1 flex flex-col items-center justify-center bg-gray-50">
          <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <h2 className="text-lg font-medium text-gray-600 mb-2">
            {viewState === 'not_found' ? 'Canvas not found' : 'Something went wrong'}
          </h2>
          <p className="text-sm text-gray-400 mb-4">
            {viewState === 'not_found' ? 'This shared link may have expired or been removed.' : 'Failed to load the shared canvas.'}
          </p>
          <Button variant="primary" size="md" onClick={() => navigate('/dashboard')}>
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  // Loading
  if (viewState === 'loading' || !canvasData) {
    return (
      <div className="h-screen flex flex-col">
        <SharedHeader />
        <div className="flex-1 flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <Spinner size="lg" className="mx-auto mb-3" />
            <p className="text-sm text-gray-500">Loading shared canvas...</p>
          </div>
        </div>
      </div>
    );
  }

  // Viewing — show read-only canvas with duplicate button
  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 flex items-center justify-between px-3 md:px-4 h-11 md:h-12 flex-shrink-0">
        <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
          <Link to="/dashboard" className="flex items-center hover:opacity-80 transition-opacity">
            <Logo size="sm" />
          </Link>
          <div className="h-4 w-px bg-gray-200" />
          <div className="flex items-center gap-1.5 min-w-0">
            <Eye className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            <span className="text-sm font-semibold text-gray-800 truncate">{canvasName}</span>
            <span className="text-xs text-gray-400 flex-shrink-0 hidden md:inline">Read only</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
          {orgId && (
            <Button
              variant={duplicateSuccess ? 'primary' : 'secondary'}
              size="sm"
              onClick={handleDuplicate}
              isLoading={isDuplicating}
              disabled={duplicateSuccess}
              className="flex items-center gap-1.5"
            >
              <Copy className="w-3.5 h-3.5" />
              <span className="hidden md:inline">
                {duplicateSuccess ? 'Duplicated!' : 'Duplicate to My Workspace'}
              </span>
              <span className="md:hidden">
                {duplicateSuccess ? 'Done!' : 'Duplicate'}
              </span>
            </Button>
          )}
          <UserMenu />
        </div>
      </div>

      {/* Canvas */}
      <div className="flex-1 relative">
        <FlowCanvas
          canvasId={canvasData.id}
          readOnly
          initialData={canvasData}
        />
      </div>
    </div>
  );
}

// Minimal header for loading/error states
function SharedHeader({ canvasName }: { canvasName?: string }) {
  return (
    <div className="bg-white border-b border-gray-200 flex items-center justify-between px-3 md:px-4 h-11 md:h-12 flex-shrink-0">
      <div className="flex items-center gap-2 md:gap-3">
        <Link to="/dashboard" className="flex items-center hover:opacity-80 transition-opacity">
          <Logo size="sm" />
        </Link>
        {canvasName && (
          <>
            <div className="h-4 w-px bg-gray-200" />
            <span className="text-sm font-semibold text-gray-800 truncate">{canvasName}</span>
          </>
        )}
      </div>
      <UserMenu />
    </div>
  );
}
